import { Auth } from 'aws-amplify';
import { isAfter, isFuture, isSameDay, parseISO } from 'date-fns';
import { adapters } from '@garner-health/api-client';
import { GarnerClientsClientV1 } from '@garner-health/api-client/svc-account/clients/clients-v1';
import { GarnerMembershipServiceV1, UpsertMemberRequest } from '@garner-health/api-client/membership-v1';
import { Item } from '../helpers/common';
import { getTimeFrameForPlan } from '../helpers/plan-timeframe-helper';
import logger from '../logging';
import { Client, Member, MemberGroup, Plan } from '../models';
import { CoverageTier, Relationship } from '../models/enums';
import { partition } from '../util';

const log = logger(__filename);

export const DEFAULT_HEADERS = {
  'Garner-Application-ID': 'ui-eligibility',
  'Garner-Application-Version': GARNER_VERSION,
};

interface EligibilityPeriods {
  start_date: string;
  end_date?: string;
  plan_id?: number;
  plan_start_date?: string;
  plan_end_date?: string;
  coverage_tier?: 'Employee' | 'EmployeeAndSpouse' | 'EmployeeAndChildren' | 'EmployeeAndFamily';
}

function formatMember(member: Item<Awaited<ReturnType<typeof getClientMemberGroups>>>): Member {
  const latestEligibility = member.eligibility_periods.reduce((a, b) => {
    return parseISO(a.start_date) > parseISO(b.start_date) ? a : b;
  });

  if (!latestEligibility.end_date) latestEligibility.end_date = latestEligibility.plan_end_date!;
  const sameEndDateAsPlan = latestEligibility.end_date === latestEligibility.plan_end_date;
  const endDateInFuture = isFuture(parseISO(latestEligibility.end_date));
  const benefitEndDate = sameEndDateAsPlan && endDateInFuture ? undefined : latestEligibility.end_date;

  return {
    id: member.id,
    externalEmployeeId: member.external_employee_id,
    firstName: member.first_name,
    lastName: member.last_name,
    birthDate: member.date_of_birth!,
    ssn: member.identification_code!,
    email: member.email,
    phone: member.home_phone_number,
    addressLine1: member.address_line_1,
    addressLine2: member.address_line_2,
    city: member.city,
    state: member.state,
    zipCode: member.zip_code,
    relationshipCode: member.relationship_code as Relationship,
    benefitStartDate: latestEligibility.start_date,
    benefitEndDate,
  };
}

/**
 * Furthest period is defined as the start date on a period that is the furthest in time
 * @param plans
 * @param periods
 * @returns furthest plan
 */
export function getPlanForFurthestPeriod(plans: Plan[], periods: EligibilityPeriods[]) {
  const furthestPeriod = periods.reduce((a, b) => (isAfter(parseISO(a.start_date), parseISO(b.start_date)) ? a : b));
  return plans.find(p => p.id === furthestPeriod!.plan_id);
}

export async function getClientsFullTree(clientId: number): Promise<Client> {
  const [members, client] = await Promise.all([getClientMemberGroups(clientId), getClient(clientId)]);
  const plans = client.plans.map(plan => ({
    id: plan.planId,
    name: plan.planName,
    startDate: plan.startDate,
    endDate: plan.endDate,
  }));

  const [primaries, dependents] = partition(members, m => (m.relationship_code === Relationship.SELF ? 0 : 1));
  if (!client.clientOrgs[0]) throw new Error('Missing client organization');
  const organizationId = client.clientOrgs[0].name;

  const groups = new Map<string, MemberGroup>();
  for (const primary of primaries) {
    if (!primary.eligibility_periods.length) {
      log.warn({ memberId: primary.id }, 'Primary member %s has no eligibility periods. Skipping');
      continue;
    }

    const plan = getPlanForFurthestPeriod(plans, primary.eligibility_periods);

    if (!plan) {
      log.warn(
        { memberId: primary.id },
        'Primary member %s latest eligibility period does not match to a client plan. Skipping',
      );
      continue;
    }

    groups.set(primary.external_employee_id, {
      id: primary.external_employee_id,
      clientId: primary.client_id!,
      coverageTier: primary.coverage_tier as CoverageTier,
      benefitPlanTimeFrame: getTimeFrameForPlan(plan),
      benefitPlanName: plan.name,
      benefitPlanId: plan.id,
      invoiceGroup: primary.invoice_group,
      organizationId,
      primary: formatMember(primary),
      dependents: [],
      size: 1,
    });
  }

  for (const member of dependents) {
    const group = groups.get(member.external_employee_id);
    if (!group) {
      log.warn({ externalEmployeeId: member.external_employee_id }, 'No primary found for employee ID');
      continue;
    }

    if (!member.eligibility_periods.length) {
      log.warn({ memberId: member.id }, 'Dependent member %s has no eligibility periods. Skipping');
      continue;
    }

    const dependent = formatMember(member);
    dependent.isAddressSameAsPrimary = propsEqual(group.primary, dependent, [
      'addressLine1',
      'addressLine2',
      'city',
      'state',
      'zipCode',
    ]);

    group.dependents.push(dependent);
    const currentDate = new Date();
    if (
      !dependent.benefitEndDate ||
      isFuture(parseISO(dependent.benefitEndDate)) ||
      isSameDay(parseISO(dependent.benefitEndDate), currentDate)
    ) {
      group.size++;
    }
  }

  return {
    id: client.id,
    name: client.name,
    organizationId,
    plans,
    memberGroups: Array.from(groups.values()).sort(compareMemberGroups),
  };
}

export function propsEqual<T>(a: T, b: T, props: (keyof T)[]) {
  return props.every(prop => a[prop] === b[prop]);
}

export function compareMemberGroups({ primary: a }: MemberGroup, { primary: b }: MemberGroup): number {
  if (!!a.benefitEndDate === !!b.benefitEndDate) {
    if (a.lastName === b.lastName) {
      if (a.firstName === b.firstName) {
        return 0;
      } else {
        return a.firstName < b.firstName ? -1 : 1;
      }
    } else {
      return a.lastName < b.lastName ? -1 : 1;
    }
  } else {
    return a.benefitEndDate ? 1 : -1;
  }
}

export async function getClientMemberGroups(clientId: number) {
  const memberService = new GarnerMembershipServiceV1(await getServiceOpts());

  return memberService
    .get('/members', {
      query: {
        client_id: clientId,
        offset: 0,
        limit: 99999,
      },
    })
    .onStatus(200, r => r.json.members);
}

export async function getClient(clientId: number) {
  const accountService = new GarnerClientsClientV1(await getServiceOpts());

  return accountService
    .get('/client/{clientId}', {
      params: {
        clientId: clientId,
      },
    })
    .onStatus(200, r => r.json)
    .onStatus(404, ({ json: { message } }) => {
      throw new Error(message);
    });
}

export async function upsertMember(memberId: string, apiMember: UpsertMemberRequest['json']) {
  const memberService = new GarnerMembershipServiceV1(await getServiceOpts());

  return memberService
    .put('/members/{memberId}', {
      params: {
        memberId: memberId,
      },
      query: {
        client_id: apiMember.client_id,
      },
      json: apiMember,
    })
    .onStatus(200, r => r.json)
    .onStatus(409, ({ json: { message } }) => {
      throw new Error(message);
    });
}

export async function getServiceOpts() {
  const session = await Auth.currentSession();
  const authToken = session.getIdToken().getJwtToken();

  return {
    baseUrl: `${location.href}api`,
    adapter: adapters.fetch,
    baseHeaders: { ...DEFAULT_HEADERS, Authorization: `Bearer ${authToken}` },
  };
}
